import React, { useMemo, useState } from 'react';
import { dashboardMuted, dashboardStatCard, dashboardStatLabel } from '../../../theme/dashboardLayout';
import { coverageDisclaimer } from '../mapResponses';
import { scoreBandColor, scoreBandLabelColor } from '../scoreBandColor';
import { AiMomentumDashboardResponse } from '../types';
import {
  ALL_WEEKDAYS,
  denseParticipationSeries,
  toggleWeekday,
  WEEKDAY_OPTIONS,
  WEEKDAYS_ONLY,
  weekdayParticipationPercents,
} from '../weekdayFilter';
import CoverageDayColorLegend from './CoverageDayColorLegend';
import ParticipationRow from './ParticipationRow';
import { setsEqual, weekdayChipClass, weekdayDayClass } from './weekdayFilterUi';

interface Props {
  momentum: AiMomentumDashboardResponse;
  participation: AiMomentumDashboardResponse['signal_participation'];
}

function SignalParticipationSection({ momentum, participation }: Props): React.ReactElement {
  const [weekdays, setWeekdays] = useState<ReadonlySet<number>>(() => new Set(ALL_WEEKDAYS));
  const chartDaily = useMemo(
    () =>
      denseParticipationSeries(
        momentum.daily,
        participation.reporting_window_days,
        momentum.anchor_date,
        weekdays,
      ),
    [momentum.daily, momentum.anchor_date, participation.reporting_window_days, weekdays],
  );
  const weekdayPercents = useMemo(
    () =>
      weekdayParticipationPercents(
        momentum.daily,
        participation.reporting_window_days,
        momentum.anchor_date,
      ),
    [momentum.daily, momentum.anchor_date, participation.reporting_window_days],
  );

  return (
    <section>
      <article id="signal-participation" className={`${dashboardStatCard} scroll-mt-24 hover:!translate-y-0`}>
        <h2 className={dashboardStatLabel}>Signal participation</h2>
        <p className={`mt-1 ${dashboardMuted}`}>
          Average daily share of people with each signal, last {participation.reporting_window_days}{' '}
          {participation.reporting_window_days === 1 ? 'day' : 'days'}
        </p>
        <div
          role="group"
          aria-label="Days to show"
          className="my-5 flex w-full flex-wrap items-center justify-center gap-2"
        >
          <div className="inline-flex rounded-full bg-portal-header-wash p-0.5">
            <button
              type="button"
              className={weekdayChipClass(weekdays.size === ALL_WEEKDAYS.size)}
              onClick={() => setWeekdays(new Set(ALL_WEEKDAYS))}
            >
              All
            </button>
            <button
              type="button"
              className={weekdayChipClass(setsEqual(weekdays, WEEKDAYS_ONLY))}
              onClick={() => setWeekdays(new Set(WEEKDAYS_ONLY))}
            >
              Weekdays
            </button>
          </div>
          <div className="inline-flex rounded-full bg-portal-header-wash p-0.5">
            {WEEKDAY_OPTIONS.map((option) => {
              const percent = weekdayPercents[option.day] ?? 0;
              const fill = scoreBandColor(percent);
              return (
                <button
                  key={option.day}
                  type="button"
                  aria-label={`${option.name}, ${percent} percent participation`}
                  aria-pressed={weekdays.has(option.day)}
                  title={`${option.name} · ${percent}%`}
                  className={weekdayDayClass()}
                  style={{
                    backgroundColor: fill,
                    color: scoreBandLabelColor(fill),
                    opacity: weekdays.has(option.day) ? 1 : 0.45,
                    boxShadow: weekdays.has(option.day) ? '0 0 0 2px #004b4d' : undefined,
                  }}
                  onClick={() => setWeekdays(toggleWeekday(weekdays, option.day))}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
        <CoverageDayColorLegend />
        <div className="flex min-h-0 flex-col justify-between gap-5">
          <ParticipationRow
            label="Wearables"
            points={chartDaily.map((point) => ({
              date: point.date,
              percent: point.participation.wearables_percent,
            }))}
          />
          <ParticipationRow
            label="Face"
            points={chartDaily.map((point) => ({
              date: point.date,
              percent: point.participation.face_percent,
            }))}
          />
          <ParticipationRow
            label="Voice"
            points={chartDaily.map((point) => ({
              date: point.date,
              percent: point.participation.voice_percent,
            }))}
          />
        </div>
        <p className={`mt-5 ${dashboardMuted}`}>
          <span aria-hidden="true">* </span>
          {coverageDisclaimer(participation.reporting_window_days)}
        </p>
      </article>
    </section>
  );
}

export default SignalParticipationSection;
